import { useEffect, useRef, useState } from "react";

/**
 * GlobalHelpTooltip
 * Renders a single tooltip that follows the cursor for any element with `data-help="..."`.
 * Uses event delegation at the document level so we don't have to wrap every button/input.
 *
 * Usage: just add `data-help="Texto que explica para qué es"` to any element.
 */
export default function GlobalHelpTooltip() {
  const [state, setState] = useState({ visible: false, text: "", x: 0, y: 0 });
  const lastTargetRef = useRef(null);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    const SHOW_DELAY = 300;

    const findHelpEl = (el) => {
      let node = el;
      while (node && node.nodeType === 1) {
        if (node.dataset && node.dataset.help) return node;
        node = node.parentElement;
      }
      return null;
    };

    const updatePosition = (target, clientX, clientY) => {
      const rect = target.getBoundingClientRect();
      // Prefer above the element, fallback below if there's no room.
      const x = Math.min(window.innerWidth - 16, Math.max(8, clientX || rect.left + rect.width / 2));
      const aboveSpace = rect.top;
      const y = aboveSpace > 56 ? rect.top - 8 : rect.bottom + 8;
      const placeAbove = aboveSpace > 56;
      return { x, y, placeAbove };
    };

    const handleEnter = (e) => {
      const target = findHelpEl(e.target);
      if (!target) return;
      lastTargetRef.current = target;
      clearTimeout(hideTimerRef.current);
      clearTimeout(showTimerRef.current);
      showTimerRef.current = setTimeout(() => {
        const text = target.dataset.help;
        if (!text) return;
        const pos = updatePosition(target, e.clientX, e.clientY);
        setState({ visible: true, text, x: pos.x, y: pos.y, placeAbove: pos.placeAbove });
      }, SHOW_DELAY);
    };

    const handleMove = (e) => {
      const target = findHelpEl(e.target);
      if (target && target === lastTargetRef.current && state.visible) {
        const pos = updatePosition(target, e.clientX, e.clientY);
        setState((s) => ({ ...s, x: pos.x, y: pos.y, placeAbove: pos.placeAbove }));
      }
    };

    const handleLeave = (e) => {
      const target = findHelpEl(e.target);
      if (!target) return;
      clearTimeout(showTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        if (lastTargetRef.current === target) {
          setState((s) => ({ ...s, visible: false }));
          lastTargetRef.current = null;
        }
      }, 80);
    };

    const handleFocus = (e) => {
      const target = findHelpEl(e.target);
      if (!target) return;
      lastTargetRef.current = target;
      const pos = updatePosition(target);
      setState({ visible: true, text: target.dataset.help, x: pos.x, y: pos.y, placeAbove: pos.placeAbove });
    };

    const handleBlur = () => {
      setState((s) => ({ ...s, visible: false }));
      lastTargetRef.current = null;
    };

    const handleScroll = () => {
      setState((s) => (s.visible ? { ...s, visible: false } : s));
    };

    document.addEventListener("mouseover", handleEnter, true);
    document.addEventListener("mousemove", handleMove, true);
    document.addEventListener("mouseout", handleLeave, true);
    document.addEventListener("focusin", handleFocus, true);
    document.addEventListener("focusout", handleBlur, true);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mouseover", handleEnter, true);
      document.removeEventListener("mousemove", handleMove, true);
      document.removeEventListener("mouseout", handleLeave, true);
      document.removeEventListener("focusin", handleFocus, true);
      document.removeEventListener("focusout", handleBlur, true);
      window.removeEventListener("scroll", handleScroll, true);
      clearTimeout(showTimerRef.current);
      clearTimeout(hideTimerRef.current);
    };
  }, [state.visible]);

  if (!state.visible || !state.text) return null;

  const transform = state.placeAbove
    ? "translate(-50%, -100%)"
    : "translate(-50%, 0)";

  return (
    <div
      role="tooltip"
      data-testid="global-help-tooltip"
      style={{
        position: "fixed",
        left: state.x,
        top: state.y,
        transform,
        zIndex: 9999,
        pointerEvents: "none",
      }}
      className="max-w-xs"
    >
      <div className="bg-ink text-white text-xs leading-snug px-3 py-2 shadow-lg border border-ink/80">
        {state.text}
      </div>
      <div
        className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-ink rotate-45"
        style={state.placeAbove ? { bottom: -4 } : { top: -4 }}
      />
    </div>
  );
}
